/**
 * Credential Verification API Service
 *
 * All credential operations go through the backend API.
 * The backend handles: contract loading, TX building, Lucid initialization.
 * The frontend handles: wallet signing (CIP-30) and submitting.
 */

const API_BASE = "/api/credentials";
const ACCESS_TOKEN_KEY = "mm_access_token";

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (typeof window === "undefined") return headers;
  const token = window.localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// Generalized Credential Cases (CP-003 scaffold)
// =============================================================================

export async function createCredentialCase(data: {
  templateId: string;
  holderWallet: string;
  networkId: number;
  payload: Record<string, unknown>;
}): Promise<{
  success: boolean;
  caseId?: string;
  status?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/cases`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      caseId?: string;
      status?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

export async function getCredentialCase(caseId: string): Promise<{
  success: boolean;
  case?: {
    id: string;
    status: string;
    events: Array<{ type: string; createdAt: string; actor?: string }>;
  };
  error?: string;
}> {
  try {
    const response = await fetch(
      `${API_BASE}/cases/${encodeURIComponent(caseId)}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );
    return (await response.json()) as {
      success: boolean;
      case?: {
        id: string;
        status: string;
        events: Array<{ type: string; createdAt: string; actor?: string }>;
      };
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

// =============================================================================
// TX Building & Submission
// =============================================================================

/**
 * Build an unsigned TX to mint Depository Note(s)
 *
 * Backend loads the credential contract, parameterizes it,
 * queries user UTxOs, builds the TX, and returns unsigned CBOR.
 */
export async function buildDepositTx(data: {
  address: string;
  networkId: number;
  quantity?: number;
  network?: string;
  walletName?: string;
  pkh?: string;
}): Promise<{
  success: boolean;
  txCbor?: string;
  txHash?: string;
  policyId?: string;
  scriptAddress?: string;
  depositLovelace?: string;
  emulatorMode?: boolean;
  userAddress?: string;
  userPkh?: string;
  userWalletName?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/build-deposit-tx`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      txCbor?: string;
      txHash?: string;
      policyId?: string;
      scriptAddress?: string;
      depositLovelace?: string;
      emulatorMode?: boolean;
      userAddress?: string;
      userPkh?: string;
      userWalletName?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Submit a signed credential TX
 *
 * After the frontend signs the CBOR with the user's CIP-30 wallet,
 * this submits it via the backend's provider connection.
 */
export async function submitCredentialTx(data: {
  signedTxCbor: string;
  networkId?: number;
  network?: string;
  pkh?: string;
  address?: string;
  walletName?: string;
}): Promise<{
  success: boolean;
  txHash?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/submit-tx`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      txHash?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

// =============================================================================
// Verification Process
// =============================================================================

/**
 * Start a new ID verification request
 * Creates a DB record and sends verification email
 */
export async function startVerificationRequest(data: {
  userId: string;
  email: string;
  requesterAlias?: string;
}): Promise<{
  success: boolean;
  processId?: string;
  refCode?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/request`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      processId?: string;
      refCode?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Get Stripe Identity verification session
 * Returns client secret for Stripe.verifyIdentity()
 */
export async function getStripeVerificationSession(data: {
  address: string;
  userId: string;
  pkh: string;
  networkId: number;
  network?: string;
  devMode?: boolean;
}): Promise<{
  success: boolean;
  clientSecret?: string;
  alreadyVerified?: boolean;
  status?: string;
  blessedTx?: string | null;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/stripe-session`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      clientSecret?: string;
      alreadyVerified?: boolean;
      status?: string;
      blessedTx?: string | null;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Check verification status
 * Polls backend for updated process state (blessed_tx, etc.)
 */
export async function checkVerificationStatus(data: {
  address: string;
  pkh: string;
  networkId: number;
  network?: string;
}): Promise<{
  success: boolean;
  accountInfo?: {
    user_id: string;
    activeProcesses: Array<{ name: string; details: any }>;
  };
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/status`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      accountInfo?: {
        user_id: string;
        activeProcesses: Array<{ name: string; details: any }>;
      };
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Get wallet assets used by the credential flow (refresh hydration).
 */
export async function getCredentialWalletAssets(data: {
  address?: string;
  pkh?: string;
  networkId: number;
  network?: string;
}): Promise<{
  success: boolean;
  userAddress?: string | null;
  userPkh?: string | null;
  assets?: Record<string, Record<string, number>>;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/wallet-assets`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      userAddress?: string | null;
      userPkh?: string | null;
      assets?: Record<string, Record<string, number>>;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Request server-side blessing (admin partial-sign) for DID minting
 *
 * Backend builds TX that mints DID token + burns Depository Note,
 * signs with vendor key, and returns the partially-signed CBOR.
 * Frontend then adds user signature and submits.
 */
export async function requestBlessing(data: {
  address: string;
  pkh: string;
  networkId: number;
  network?: string;
}): Promise<{
  success: boolean;
  blessedTx?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/bless`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as {
      success: boolean;
      blessedTx?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Log a credential transaction to the orderbook
 */
export async function logCredentialTransaction(data: {
  email?: string;
  address: string;
  userId: string;
  pkh: string;
  networkId: number;
  network?: "emulator" | "preview" | "preprod" | "mainnet";
  txHash: string;
  quantity: number;
  paymentType: string;
  paymentRef: string;
  policyId?: string;
  refCode?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/log-tx`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return (await response.json()) as { success: boolean; error?: string };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Reset emulator state for credential deposit retry
 * Only works in emulator mode — resets the emulator and re-initializes wallets
 */
export async function resetCredentialDeposit(pkh?: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE}/reset-deposit`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ pkh: pkh || "" }),
    });
    return (await response.json()) as {
      success: boolean;
      message?: string;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Get credential vending signer configuration.
 * Useful for confirming contract vending PKH vs backend secondary signer PKH.
 */
export async function getCredentialSignerConfig(
  networkId: number,
  network?: "emulator" | "preview" | "preprod",
): Promise<{
  success: boolean;
  network?: string;
  policyId?: string;
  vendingPkh?: string;
  secondarySignerPkh?: string | null;
  secondarySignerAddress?: string | null;
  signerMatchesVendingPkh?: boolean | null;
  error?: string;
}> {
  try {
    const query = new URLSearchParams({ networkId: String(networkId) });
    if (network) query.set("network", network);

    const response = await fetch(`${API_BASE}/config?${query.toString()}`);
    return (await response.json()) as {
      success: boolean;
      network?: string;
      policyId?: string;
      vendingPkh?: string;
      secondarySignerPkh?: string | null;
      secondarySignerAddress?: string | null;
      signerMatchesVendingPkh?: boolean | null;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Validate a referral/invite code
 */
export async function validateReferralCode(refCode: string): Promise<{
  success: boolean;
  referralInfo?: any;
  error?: string;
}> {
  try {
    const response = await fetch(
      `${API_BASE}/validate-ref/${encodeURIComponent(refCode)}`,
    );
    return (await response.json()) as {
      success: boolean;
      referralInfo?: any;
      error?: string;
    };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}
